// Biometric authentication helper
// Gracefully degrades when not on a supported platform (e.g. web)
// Uses capacitor-native-biometric for Android Face/Fingerprint, iOS Face ID/Touch ID

import { Capacitor } from '@capacitor/core';
import { t } from '@/i18n';

let NativeBiometric = null;
let Preferences = null;

// Lazy-load plugins so they don't break web builds
async function loadPlugins() {
  if (!Capacitor.isNativePlatform()) {
    return false;
  }
  try {
    if (!NativeBiometric) {
      const mod = await import('capacitor-native-biometric');
      NativeBiometric = mod.NativeBiometric;
    }
    if (!Preferences) {
      const mod = await import('@capacitor/preferences');
      Preferences = mod.Preferences;
    }
    return true;
  } catch (e) {
    console.warn('[Biometric] Plugin not available:', e?.message);
    return false;
  }
}

const ENABLED_KEY = 'biometric_enabled';

// THE KEYCHAIN ENTRY, WRITTEN ONCE (26 September 2026, v18.59).
//
// This string was typed out three times - in setCredentials, getCredentials
// and deleteCredentials. Three copies of the address of the one thing this
// module exists to look after. If any of them ever drifted, save would write
// to one entry and sign-in would read an empty other one, and the symptom
// would be "biometric login just stopped working" with nothing in a log.
//
// It is also the old brand name. Left exactly as it is on purpose: changing
// it orphans the credentials already saved on every phone that has used this,
// and there is no migration for a keychain entry you can no longer name.
const KEYCHAIN_SERVER = 'familycart.app';

// STORAGE_KEY was declared here and never read by anything. Removed rather
// than left looking like it meant something.

/**
 * Check if biometric authentication is available on this device.
 * Returns: { available, biometryType }
 */
export async function isBiometricAvailable() {
  const ok = await loadPlugins();
  if (!ok) return { available: false, reason: 'not-native' };

  try {
    const result = await NativeBiometric.isAvailable();
    return {
      available: result.isAvailable,
      biometryType: result.biometryType, // 1=touch, 2=face, etc.
      reason: result.errorCode || null
    };
  } catch (e) {
    return { available: false, reason: e?.message || 'unknown' };
  }
}

/**
 * Check if user has previously enabled biometric login.
 */
export async function isBiometricEnabled() {
  const ok = await loadPlugins();
  if (!ok) return false;
  try {
    const result = await Preferences.get({ key: ENABLED_KEY });
    return result.value === 'true';
  } catch {
    return false;
  }
}

/**
 * Save credentials securely after successful login.
 * Stores email + a token (we use the user's password) protected by biometric.
 */
export async function saveBiometricCredentials(email, password) {
  const ok = await loadPlugins();
  if (!ok) throw new Error('Biometric not available');

  // Check biometric is available
  const avail = await NativeBiometric.isAvailable();
  if (!avail.isAvailable) throw new Error('Biometric hardware not available');

  // Store credentials in iOS Keychain / Android Keystore
  await NativeBiometric.setCredentials({
    username: email,
    password: password,
    server: KEYCHAIN_SERVER
  });

  await Preferences.set({ key: ENABLED_KEY, value: 'true' });
  return true;
}

/**
 * Authenticate with biometric and retrieve saved credentials.
 * Returns { email, password } or throws if cancelled/failed.
 */
export async function authenticateBiometric() {
  const ok = await loadPlugins();
  if (!ok) throw new Error('Biometric not available');

  // Trigger biometric prompt.
  //
  // THESE FOUR STRINGS ARE READ BY A PERSON, in the operating system's own
  // dialog, and until v18.59 all four were English in all 28 languages.
  // Nothing in this repo could see them: they are not JSX, so the literal
  // checker never looks here, and they are not a backend subject line
  // either. A module of plain functions was the one shape neither checker
  // covers - which is the same lesson as the IIFE and the prop object, in a
  // third place.
  //
  // `t` is imported directly rather than through useTranslation because this
  // is not a component. AuthContext does the same for its session-expired
  // dialog.
  await NativeBiometric.verifyIdentity({
    reason: t('biometric.reason'),
    title: t('biometric.title'),
    subtitle: t('biometric.subtitle'),
    description: t('biometric.description')
  });

  // Retrieve stored credentials
  const creds = await NativeBiometric.getCredentials({ server: KEYCHAIN_SERVER });
  return {
    email: creds.username,
    password: creds.password
  };
}

/**
 * Disable biometric login and remove stored credentials.
 */
export async function disableBiometric() {
  const ok = await loadPlugins();
  if (!ok) return;
  try {
    await NativeBiometric.deleteCredentials({ server: KEYCHAIN_SERVER });
    await Preferences.set({ key: ENABLED_KEY, value: 'false' });
  } catch (e) {
    console.warn('[Biometric] Disable failed:', e);
  }
}
